import React from 'react';
import { CheckIcon, InfoIcon, SparkleIcon } from 'lucide-react';
import type { Section } from '../types/onboarding';
import { withPlaceholders } from './Placeholder';
import { MediaPlaceholder } from './MediaPlaceholder';
interface SectionCardProps {
  section: Section;
  done: boolean;
  onToggleDone: () => void;
  children: React.ReactNode;
  /** White-glove sections get an emerald-tinted "we handle this" treatment. */
  tone?: 'default' | 'together';
}
export function SectionCard({
  section,
  done,
  onToggleDone,
  children,
  tone = 'default'
}: SectionCardProps) {
  const Icon = section.icon;
  const together = tone === 'together';
  const number = String(section.number).padStart(2, '0');
  return <section id={section.id} aria-labelledby={`${section.id}-title`} className={['group scroll-mt-28 rounded-2xl border text-white shadow-[0_18px_40px_-24px_rgba(0,0,0,0.9)] transition-all duration-200 hover:-translate-y-1 sm:scroll-mt-24', together ? 'border-[#10b981]/30 bg-[#0f1512] hover:border-[#10b981]/60' : 'border-white/10 bg-[#111214] hover:border-[#10b981]/40', 'hover:shadow-[0_0_0_1px_rgba(16,185,129,0.35),0_18px_50px_-20px_rgba(16,185,129,0.22)]'].join(' ')} data-id="element-95" data-mp="zi73h5ntrzr9">
      <div className="p-5 sm:p-8" data-id="element-96" data-mp="o66d9f84wgqw">
        <div className="flex items-start gap-4" data-id="element-97" data-mp="j5s7k3u09iw1">
          <span aria-hidden="true" className={['flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border text-[#10b981] transition-colors duration-200 sm:h-12 sm:w-12', together ? 'border-[#10b981]/40 bg-[#10b981]/15' : 'border-white/10 bg-white/[0.06]'].join(' ')} data-id="element-98" data-mp="j7cyzkl0wlpo">
            <Icon className="h-5 w-5 sm:h-6 sm:w-6" data-id="element-99" data-mp="gluoxfw7li5" />
          </span>

          <div className="min-w-0 flex-1" data-id="element-100" data-mp="3rk0rknigp1cl">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2" data-id="element-101" data-mp="3rn6im8aniday">
              <span className="font-mono text-[13px] font-semibold tracking-tight text-[#10b981]" data-id="element-102" data-mp="3mmipnh2v3me3">
                {number} /
              </span>
              {together && <span className="inline-flex items-center gap-1.5 rounded-full border border-[#10b981]/40 bg-[#10b981]/10 px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.18em] text-[#6ee7b7]" data-id="element-103" data-mp="3hyhl6kls8dg9">
                  <SparkleIcon className="h-3 w-3" aria-hidden="true" data-id="element-104" data-mp="380bku7fmjfdi" />
                  White-glove
                </span>}
            </div>

            <h2 id={`${section.id}-title`} className="mt-2 font-mono text-[19px] font-bold leading-snug tracking-tight text-white sm:text-2xl" data-id="element-105" data-mp="4ijxx7gchlm6">
              {section.title}
            </h2>
            <p className="mt-2 text-[15px] leading-relaxed text-white/70 sm:text-base" data-id="element-106" data-mp="3qxcw29zrubpw">
              {section.purpose}
            </p>
          </div>
        </div>

        <div className="mt-7 space-y-6" data-id="element-107" data-mp="n91eyertesrp">{children}</div>

        {section.mediaSlots.length > 0 && <div className="mt-7 grid gap-3" data-id="element-108" data-mp="27zgh0blmewq7">
            {section.mediaSlots.map(slot => <MediaPlaceholder key={slot} label={slot} data-id="element-109" data-mp="230dbyr5n4qi8" />)}
          </div>}

        {section.notes && section.notes.length > 0 && <div className="mt-6 space-y-3" data-id="element-110" data-mp="2hoqc80brgvij">
            {section.notes.map(note => <div key={note} className="flex gap-3 rounded-xl border border-white/10 bg-white/[0.05] px-4 py-3.5" data-id="element-111" data-mp="2crhs3u71yhen">
                <InfoIcon className="mt-0.5 h-4 w-4 shrink-0 text-[#10b981]" aria-hidden="true" data-id="element-112" data-mp="1k1rcthtop9gy" />
                <p className="text-sm leading-relaxed text-white/85" data-id="element-113" data-mp="1z0czhc23qpdt">
                  {withPlaceholders(note)}
                </p>
              </div>)}
          </div>}

        <div className="mt-7 border-t border-white/10 pt-6" data-id="element-114" data-mp="3acm9b658spfu">
          <button type="button" onClick={onToggleDone} aria-pressed={done} className={['flex min-h-[48px] w-full items-center justify-center gap-2.5 rounded-lg px-5 text-[15px] font-semibold transition-all duration-200 sm:w-auto', done ? 'bg-[#10b981] text-black hover:bg-[#34d399]' : 'border border-[#10b981]/40 text-[#10b981] hover:border-[#10b981] hover:bg-[#10b981]/10'].join(' ')} data-id="element-115" data-mp="35fdmgs95fx89">
            <span aria-hidden="true" className={['flex h-5 w-5 items-center justify-center rounded-full border', done ? 'border-black/30 bg-black/10' : 'border-[#10b981]/50'].join(' ')} data-id="element-116" data-mp="1xvjnaj1jg6ck">
              {done && <CheckIcon className="h-3.5 w-3.5" data-id="element-117" data-mp="2bo97qj1f537k" />}
            </span>
            {done ? 'Done — nice work' : 'Mark this step done'}
          </button>
        </div>
      </div>
    </section>;
}